import { Box, Button, TextButton, Tooltip } from '@wix/design-system';
import React from 'react';
import {
  useEssentials,
  useSiteContext,
  useTranslation,
  useRouterContext,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import type { getAppPageDataResponse } from '../../app-page-api';
import { ExternalLinkSmall } from '@wix/wix-ui-icons-common/lazy';
import { appModalDataHooks } from '../data-hooks';
import type { PricingBadgeProps } from '@wix/app-market-components';
import { AppInfoBadge } from '@wix/app-market-components';
import { isAppWithBenefits } from '@wix/app-market-services';
import { AppPageCTAs } from '../../app-page-ctas';
import { AppPageType } from '@wix/app-market-core-components-types';
import { appMarketAppPageClick } from '@wix/bi-logger-app-market-data/v2';
import styles from '../app-modal.scss';

interface AppModalContentSideActionsProps {
  showTermsAndConditions: boolean;
  appModalData: getAppPageDataResponse;
  shouldShowPermissions: boolean;
  showAppPageLink: boolean;
  onClose: () => void;
  origin: string;
}
const AppModalContentSideActions = ({
  showTermsAndConditions,
  appModalData,
  shouldShowPermissions,
  showAppPageLink,
  onClose,
  origin,
}: AppModalContentSideActionsProps) => {
  const { experiments, biLogger } = useEssentials();
  const { t } = useTranslation();
  const { isPremium, languageCode } = useSiteContext();
  const { router } = useRouterContext();

  const appPageType = AppPageType.ONE_STEP_SLIM;

  const handleLearnMoreClick = () => {
    router.navigateTo({
      path: RoutePath.APP_PAGE,
      payload: {
        slug: appModalData.app.slug,
        referral: `in_context_${origin}`,
      },
    });
    biLogger.report(
      appMarketAppPageClick({
        app_id: appModalData.app.id,
        cta: 'view_app_full_details',
        location: 'slim_app_page',
        app_page_type: appPageType,
      }),
    );
  };

  return (
    <Box
      direction="horizontal"
      verticalAlign="middle"
      align="space-between"
      paddingRight={2}
    >
      {showTermsAndConditions ? (
        <Box width="100%">
          {showAppPageLink && (
            <Tooltip
              content={t('app.modal.learn-more-button.tooltip')}
              size="small"
              placement="top-start"
            >
              <TextButton
                color="white"
                size="small"
                underline="always"
                className={styles.learnMoreButton}
                onClick={handleLearnMoreClick}
                suffixIcon={<ExternalLinkSmall />}
                dataHook={appModalDataHooks.LEARN_MORE_BUTTON}
              >
                {t('app.modal.learn-more-button')}
              </TextButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        <AppInfoBadge
          languageCode={languageCode}
          experiments={experiments}
          isAppInstalled={!!appModalData?.app?.installation?.isInstalled}
          isAppBuiltIn={!!appModalData?.app?.installation?.isBuiltIn}
          isAppSetupIncomplete={
            !!appModalData?.app?.installation?.isSetupIncomplete
          }
          isAppWithBenefits={isAppWithBenefits(appModalData.app.appBadges)}
          pricing={appModalData.app.pricing as PricingBadgeProps}
          options={{
            size: 'small',
            showPremiumRequired:
              !!appModalData?.app?.installation?.requirements?.premiumSite &&
              !isPremium,
          }}
          dataHook={appModalDataHooks.APP_MODAL_FOOTER_INFO_BADGE}
        />
      )}
      <Box direction="horizontal" gap={2}>
        {!appModalData?.app?.isUpgradable && (
          <Button
            priority="secondary"
            skin="standard"
            size="small"
            onClick={() => {
              biLogger.report(
                appMarketAppPageClick({
                  app_id: appModalData.app.id,
                  cta: 'cancel',
                  location: 'slim_app_page',
                  app_page_type: appPageType,
                }),
              );
              onClose();
            }}
          >
            {t('app.modal.cancel')}
          </Button>
        )}
        <AppPageCTAs
          managedApp={appModalData.managedApp}
          app={{
            id: appModalData.app.id,
            instanceId: appModalData.app.instanceId,
            name: appModalData.app.name,
            isUpgradable: appModalData.app.isUpgradable,
            isAppWithBenefits: isAppWithBenefits(appModalData.app.appBadges),
            isInstalled: Boolean(appModalData.app.installation.isInstalled),
            isBuiltIn: Boolean(appModalData.app.installation.isBuiltIn),
            appBadges: appModalData.app.appBadges,
            notification: appModalData.notification,
            gTag: appModalData.app.gTag,
            externalPricingPageUrl:
              appModalData.pricingPlans?.externalPricingPageUrl,
          }}
          size="small"
          dataHook={appModalDataHooks.APP_MODAL_FOOTER_CTA}
          reverseOrder
          isPermissionsRequired={shouldShowPermissions}
          appPageType={appPageType}
        />
      </Box>
    </Box>
  );
};

export default AppModalContentSideActions;
